import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { getFriendlyAuthError } from '../../lib/firebaseErrors';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister: (initialError?: string) => void;
  onSwitchToForgotPassword: () => void;
  // For post-registration verification state
  verificationEmail?: string;
  showVerificationState?: boolean;
  verificationSent?: boolean;
  /**
   * Phase 3 (homepage rewrite): when set to a tab id like 'agents', a
   * successful login will navigate the browser to `/?tab=<redirectTo>`
   * so App.tsx (which parses the query param on mount) can deep-link
   * the user into that tab. Null/undefined preserves the existing
   * post-login behaviour (caller's onClose drives navigation).
   */
  redirectTo?: string | null;
}

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSwitchToRegister,
  onSwitchToForgotPassword,
  verificationEmail,
  showVerificationState: initialVerificationState,
  verificationSent: initialVerificationSent = true,
  redirectTo,
}) => {
  const { login, loginWithGoogle, sendVerificationEmail, loading, error, clearError, devBypassLogin, isFirebaseAvailable } = useAuth();
  const isProductionBuild = process.env.NODE_ENV === 'production';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  // Verification state for post-registration flow
  const [showVerificationState, setShowVerificationState] = useState(false);
  const [verificationSent, setVerificationSent] = useState(true);
  const [resendSent, setResendSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  // Initialize verification state from props when modal opens
  React.useEffect(() => {
    if (isOpen && initialVerificationState && verificationEmail) {
      setShowVerificationState(true);
      setVerificationSent(initialVerificationSent);
      setEmail(verificationEmail);
    } else if (isOpen) {
      setShowVerificationState(false);
      setVerificationSent(true);
      setResendSent(false);
    }
  }, [isOpen, initialVerificationState, verificationEmail, initialVerificationSent]);

  /**
   * Apply the optional post-login redirect by setting the URL to
   * `/?tab=<redirectTo>`. App.tsx's mount-time tab-query parser strips
   * the query and switches the active tab. Falls back to a no-op when
   * redirectTo is empty so existing call sites are unaffected.
   *
   * Uses a full navigation rather than `history.replaceState` because
   * AuthGate may swap the unauthenticated <LandingPage> for App.tsx in
   * the same tick - App's initial state must read the tab from
   * `window.location.search`. A full reload ensures App boots once with
   * the right URL.
   */
  const applyRedirectIfAny = () => {
    if (typeof redirectTo !== 'string' || redirectTo.length === 0) return;
    if (typeof window === 'undefined') return;
    // Encode just in case future callers pass a tab id with reserved chars.
    const safe = encodeURIComponent(redirectTo);
    window.location.href = `/?tab=${safe}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);

    if (!email || !password) {
      setLocalError('Please fill in all fields');
      return;
    }

    try {
      await login(email, password);
      setEmail('');
      setPassword('');
      onClose();
      applyRedirectIfAny();
    } catch (err: any) {
      // Static fallback ONLY (not err.message) - unmapped codes would leak
      // the raw "Firebase: Error (...)" string. console.error in useAuth
      // still captures err.message for debuggability.
      setLocalError(getFriendlyAuthError(err?.code, 'Sign in failed. Please try again.'));
    }
  };

  const handleGoogleLogin = async () => {
    clearError();
    setLocalError(null);

    if (!isFirebaseAvailable) {
      setLocalError('Google login requires Firebase to be configured. Use "Continue in Development Mode" below instead.');
      return;
    }

    try {
      await loginWithGoogle();
      onClose();
      applyRedirectIfAny();
    } catch (err: any) {
      if (err?.code === 'auth/new-user-must-register') {
        const friendlyMsg = getFriendlyAuthError(
          err?.code,
          'New users must sign up first and agree to the Disclaimer.',
        );
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('pending_register_error', friendlyMsg);
        }
        onSwitchToRegister(friendlyMsg);
        return;
      }
      if (
        err.code !== 'auth/popup-closed-by-user' &&
        err.code !== 'auth/cancelled-popup-request'
      ) {
        setLocalError(getFriendlyAuthError(err?.code, 'Google sign in failed. Please try again.'));
      }
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    clearError();
    setLocalError(null);
    onClose();
  };

  const displayError = localError || error;

  const onResendVerification = async () => {
    setLocalError(null);
    setResendLoading(true);
    try {
      await sendVerificationEmail();
      setResendSent(true);
    } catch (err: any) {
      setLocalError(getFriendlyAuthError(err?.code, 'Failed to send verification email. Please try again.'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleSwitchToLogin = () => {
    setShowVerificationState(false);
    setResendSent(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: 'var(--tv-bg-primary)',
          padding: '32px',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '400px',
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid var(--tv-border-primary)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '600',
              color: 'var(--tv-text-primary)',
            }}
          >
            Sign In
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--tv-text-secondary)',
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>

        {displayError && (
          <div
            style={{
              backgroundColor: 'var(--tv-error-bg, rgba(244, 67, 54, 0.1))',
              color: 'var(--tv-error-text, #f44336)',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '0.875rem',
              border:
                '1px solid var(--tv-error-border, rgba(244, 67, 54, 0.2))',
            }}
          >
            {displayError}
          </div>
        )}

        {/* Verification State View */}
        {showVerificationState && (
          <div style={{ marginBottom: '24px', textAlign: 'center' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'var(--tv-success-bg, rgba(34, 197, 94, 0.15))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--tv-success-text, #22c55e)"
                strokeWidth="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
              </svg>
            </div>
            <h3
              style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: 'var(--tv-text-primary)',
                marginBottom: '12px',
              }}
            >
              Account Created!
            </h3>
            <p
              style={{
                fontSize: '0.875rem',
                color: 'var(--tv-text-secondary)',
                marginBottom: '16px',
                lineHeight: '1.5',
              }}
            >
              We sent a verification link to<br />
              <strong style={{ color: 'var(--tv-text-primary)' }}>{email}</strong>
            </p>
            {verificationSent ? (
              <p
                style={{
                  fontSize: '0.8125rem',
                  color: 'var(--tv-text-secondary)',
                  marginBottom: '20px',
                }}
              >
                Please check your inbox and click the link to verify your email.
              </p>
            ) : (
              <div
                style={{
                  backgroundColor: 'var(--tv-warning-bg, rgba(245, 158, 11, 0.15))',
                  border: '1px solid var(--tv-warning-border, rgba(245, 158, 11, 0.4))',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '20px',
                }}
              >
                <p
                  style={{
                    fontSize: '0.8125rem',
                    color: 'var(--tv-warning-text, #fef3c7)',
                    margin: 0,
                  }}
                >
                  Account created, but we couldn't send the verification email.
                  You can request it again below.
                </p>
              </div>
            )}
            <button
              type="button"
              onClick={onResendVerification}
              disabled={resendLoading || resendSent}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'var(--accent-primary)',
                color: 'var(--on_primary)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: resendLoading || resendSent ? 'not-allowed' : 'pointer',
                opacity: resendLoading || resendSent ? 0.7 : 1,
                marginBottom: '12px',
              }}
            >
              {resendLoading ? 'Sending...' : resendSent ? 'Verification Email Sent!' : 'Resend Verification Email'}
            </button>
            <p
              style={{
                fontSize: '0.8125rem',
                color: 'var(--tv-text-secondary)',
              }}
            >
              Already verified?{' '}
              <button
                type="button"
                onClick={handleSwitchToLogin}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-primary)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Sign In
              </button>
            </p>
          </div>
        )}

        {!showVerificationState && (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'var(--tv-text-primary)',
                }}
              >
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="email"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--tv-border-primary)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  backgroundColor: 'var(--tv-bg-secondary)',
                  color: 'var(--tv-text-primary)',
                  boxSizing: 'border-box',
                }}
                placeholder="Enter your email"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'var(--tv-text-primary)',
                }}
              >
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="current-password"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--tv-border-primary)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  backgroundColor: 'var(--tv-bg-secondary)',
                  color: 'var(--tv-text-primary)',
                  boxSizing: 'border-box',
                }}
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'var(--accent-primary)',
                color: 'var(--on_primary)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                marginBottom: '16px',
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <div
                style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'var(--tv-border-primary)',
                }}
              ></div>
              <span
                style={{
                  padding: '0 16px',
                  fontSize: '0.75rem',
                  color: 'var(--tv-text-secondary)',
                }}
              >
                OR
              </span>
              <div
                style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'var(--tv-border-primary)',
                }}
              ></div>
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'white',
                color: '#000000',
                border: '1px solid var(--tv-border-primary)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>

            {/* Dev bypass: when Firebase is not configured, allow dev login */}
            {!isFirebaseAvailable && !isProductionBuild && (
              <button
                type="button"
                onClick={() => {
                  devBypassLogin();
                  onClose();
                  applyRedirectIfAny();
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'rgba(20, 184, 166, 0.15)',
                  color: '#14b8a6',
                  border: '1px solid rgba(20, 184, 166, 0.4)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  marginBottom: '16px',
                }}
              >
                Continue in Development Mode
              </button>
            )}

            <div
              style={{
                textAlign: 'center',
                fontSize: '0.8125rem',
                color: 'var(--tv-text-secondary)',
                marginBottom: '16px',
              }}
            >
              Forgot your password?{' '}
              <button
                type="button"
                onClick={onSwitchToForgotPassword}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-primary)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Reset it
              </button>
            </div>

            <div
              style={{
                textAlign: 'center',
                fontSize: '0.875rem',
                color: 'var(--tv-text-secondary)',
              }}
            >
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => onSwitchToRegister()}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-primary)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Sign Up
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
